import { Box, Button, Stack, Text } from '@mantine/core';
import { ExternalLink } from 'react-feather';
import { Icon } from '@/components/Icon/Icon';
import styles from './RedFlagWarningInfo.module.css';
import { useCallback, type FC, useEffect } from 'react';
import { formatDateAsPerLocale } from '@/helpers/date.ts';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { useClientTerminology } from '@/hooks/useClientTerminology';

// On the map the warning comes from picked feature properties, where any field
// may be missing. The card supplies all of them from the warnings endpoint.
export interface RedFlagWarningInfoProps {
  title?: string;
  coveredCircuits?: number;
  coveredDistance?: number;
  issued?: string;
  start?: string;
  end?: string;
  nwsLink?: string;
  isPopover: boolean;
}

export const RedFlagWarningInfo: FC<RedFlagWarningInfoProps> = ({
  title,
  coveredCircuits,
  coveredDistance,
  issued,
  start,
  end,
  nwsLink,
  isPopover,
}) => {
  const timezoneOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };

  const intl = useClientTerminology();

  useEffect(() => {
    // Set the nwsLink to undefined so we don't send it for every other request.
    return () => {
      TagManager.dataLayer({
        dataLayer: {
          nwsLink: undefined,
        },
      });
    };
  }, []);

  const handleReadMoreButtonClick = useCallback(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.redFlagWarning.card.warningInformation,
        nwsLink,
      },
    });
  }, [nwsLink]);

  return (
    <Stack gap="xs">
      {!isPopover && title && <Text fw={700}>{title}</Text>}
      {coveredCircuits !== undefined && coveredDistance !== undefined && (
        <span className={styles.coveredText}>
          {coveredCircuits} circuits, {coveredDistance} mi
        </span>
      )}
      <Box>
        {issued && (
          <div className={styles.dateRow}>
            <span className={isPopover ? styles.labelPopover : styles.label}>
              Issued
            </span>
            <span>
              {formatDateAsPerLocale(issued, undefined, timezoneOptions)}
            </span>
          </div>
        )}
        {start && (
          <div className={styles.dateRow}>
            <span className={isPopover ? styles.labelPopover : styles.label}>
              Start
            </span>
            <span>
              {formatDateAsPerLocale(start, undefined, timezoneOptions)}
            </span>
          </div>
        )}
        {end && (
          <div className={styles.dateRow}>
            <span className={isPopover ? styles.labelPopover : styles.label}>
              End
            </span>
            <span>
              {formatDateAsPerLocale(end, undefined, timezoneOptions)}
            </span>
          </div>
        )}
      </Box>
      {nwsLink && (
        <a
          onClick={handleReadMoreButtonClick}
          href={nwsLink}
          target="_blank"
          rel="noreferrer"
        >
          <Button variant="transparent" className={styles.readMoreButton}>
            <Icon Component={ExternalLink} color="green" size={14} />
            <Text ml="xs" size="sm">
              {intl.formatMessage({ id: 'action.readMore' })}
            </Text>
          </Button>
        </a>
      )}
    </Stack>
  );
};
